import { paths } from "config/paths";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { FC, ReactNode } from "react";
import { useGetCurrentAccountCache } from "~auth/queries/useGetCurrentAccount";
import { useGetCurrentUserCache } from "~auth/queries/useGetCurrentUser";
import {
	hasEqualOrHigherAccessThanSpecifiedRole,
	isAccountOwner as isAccountOwnerFn,
	isDatasetCreator,
	isInGroupWithEditAccess as isInGroupWithEditAccessFn,
	isMapCreator,
} from "~auth/helpers";
import { ACCOUNT_USER_ROLE } from "~auth/types/__generated/gql/graphql";
import { useGetCurrentDataset } from "~dataset-view/queries/useGetCurrentDataset";
import { useGetCurrentMap } from "~map-view/queries/useGetCurrentMap";
import { MangoLogoLoadingBackdrop } from "~ui-components/index";

export interface AuthenticatedRouteGuardProps {
	children: ReactNode;
	onlyMapCreator?: true;
	onlyDatasetCreator?: true;
	minimumAccessLevel?: `${ACCOUNT_USER_ROLE}`;
	hasMapEditAccess?: true;
	hasDatasetEditAccess?: true;
}

export const AuthenticatedRouteGuard: FC<AuthenticatedRouteGuardProps> = ({
	children,
	onlyDatasetCreator,
	onlyMapCreator,
	minimumAccessLevel,
	hasMapEditAccess,
	hasDatasetEditAccess,
}) => {
	const router = useWebRouter();
	const { isLoading: isUserLoading, data: user } = useGetCurrentUserCache();
	const { isLoading: isAccountLoading, data: account } =
		useGetCurrentAccountCache();
	const { isLoading: isMapLoading, data: map } = useGetCurrentMap();
	const { isLoading: isDatasetLoading, data: dataset } = useGetCurrentDataset();

	let isDatasetCreatorRulePassed = false;
	if (onlyDatasetCreator) {
		if (isDatasetLoading || isUserLoading) return <MangoLogoLoadingBackdrop />;

		isDatasetCreatorRulePassed = isDatasetCreator(dataset, user);
	}

	let isMapCreatorRulePassed = false;
	if (onlyMapCreator) {
		if (isMapLoading || isUserLoading) return <MangoLogoLoadingBackdrop />;
		isMapCreatorRulePassed = isMapCreator(map, user);
	}

	let isMapEditAccessRulePassed = false;
	if (hasMapEditAccess) {
		if (isMapLoading || isUserLoading || isAccountLoading)
			return <MangoLogoLoadingBackdrop />;
		const isCreator = isMapCreator(map, user);
		const isAccountOwner = isAccountOwnerFn(account);
		const isAdminWithEditAccess =
			isInGroupWithEditAccessFn(
				map?.groupsAccesses ?? [],
				user?._userGroupMembers ?? [],
			) &&
			hasEqualOrHigherAccessThanSpecifiedRole(account, ACCOUNT_USER_ROLE.ADMIN);
		isMapEditAccessRulePassed =
			isCreator || isAccountOwner || isAdminWithEditAccess;
	}

	let isDatasetEditAccessRulePassed = false;
	if (hasDatasetEditAccess) {
		if (isDatasetLoading || isUserLoading || isAccountLoading)
			return <MangoLogoLoadingBackdrop />;
		const isCreator = isDatasetCreator(dataset, user);
		const isAccountOwner = isAccountOwnerFn(account);
		const isAdminWithEditAccess =
			isInGroupWithEditAccessFn(
				dataset?.groupsAccesses ?? [],
				user?._userGroupMembers ?? [],
			) &&
			hasEqualOrHigherAccessThanSpecifiedRole(account, ACCOUNT_USER_ROLE.ADMIN);
		isDatasetEditAccessRulePassed =
			isCreator || isAccountOwner || isAdminWithEditAccess;
	}

	let isMinimumAccessLevelPassed = false;
	if (minimumAccessLevel !== undefined) {
		if (isAccountLoading) return <MangoLogoLoadingBackdrop />;

		isMinimumAccessLevelPassed = hasEqualOrHigherAccessThanSpecifiedRole(
			account,
			minimumAccessLevel,
		);
	}

	const isAllRulesFailed = [
		isDatasetCreatorRulePassed,
		isMapCreatorRulePassed,
		isMinimumAccessLevelPassed,
		isMapEditAccessRulePassed,
		isDatasetEditAccessRulePassed,
	].every((bool) => bool === false);

	if (isAllRulesFailed) {
		void router.replace(paths.unauthorized);
		return <MangoLogoLoadingBackdrop />;
	}

	return children;
};
